import { axiosV2, getError, getResponse, settings } from '../../helpers/UtilsHelper';

const { app } = settings;
const { backend } = app;
const { urls, methods } = backend;

export const UserService = {

    /**
     * Get all user accounts
     * @param {Object} params
     * @param {string} params.token
     * @param {string} params.subMethod
     * @returns {Promise<Object>}
     */
    getAll: async ({
        token = '',
        subMethod = ''
    } = {}) => {
        const currentMethod = 'UserService.getAll';
        let response = getResponse({ type: 0 });
        try {
            const headers = { authorization: `bearer ${token}` };
            const data = { conditionals: [], ordersBy: [], limit: 0 };
            response = await axiosV2?.post({
                url: `${urls?.url}${urls?.apiV1}${urls?.accounts}/`,
                data: data,
                headers,
                subMethod: currentMethod
            });
            return response;
        } catch (ex) {
            return getError({ ex: ex, response: response, currentMethod_: currentMethod, subMethod_: subMethod });
        }
    },

    /**
     * Get all users
     * @param {Object} params
     * @param {string} params.token
     * @param {Object} params.data
     * @param {string} params.subMethod
     * @returns {Promise<Object>}
     */
    getUsers: async ({
        token = '',
        data = {},
        subMethod = ''
    } = {}) => {
        const currentMethod = 'UserService.getUsers';
        let response = getResponse({ type: 0 });
        try {
            const headers = { authorization: `bearer ${token}` };
            response = await axiosV2?.post({
                url: `${urls?.url}${urls?.apiV1}${urls?.users}/`,
                data: data,
                headers,
                subMethod: currentMethod
            });
            return response;
        } catch (ex) {
            return getError({ ex: ex, response: response, currentMethod_: currentMethod, subMethod_: subMethod });
        }
    },

    /**
     * Get user account by ID
     * @param {Object} params
     * @param {string} params.id
     * @param {string} params.token
     * @param {boolean} params.getRoles
     * @param {string} params.subMethod
     * @returns {Promise<Object>}
     */
    getById: async ({
        id = '',
        token = '',
        getRoles = false,
        subMethod = ''
    } = {}) => {
        const currentMethod = 'UserService.getById';
        let response = getResponse({ type: 0 });
        try {
            const headers = { authorization: `bearer ${token}`, getRoles };
            response = await axiosV2?.get({
                url: `${urls?.url}${urls?.apiV1}${urls?.accounts}/${id}`,
                headers: headers,
                subMethod: currentMethod
            });
            return response;
        } catch (ex) {
            return getError({ ex: ex, response: response, currentMethod_: currentMethod, subMethod_: subMethod });
        }
    },

    /**
     * Get profile data (account + users + items + rates)
     * @param {Object} params
     * @param {string} params.uid
     * @param {string} params.token
     * @param {string} params.subMethod
     * @returns {Promise<Object>}
     */
    getProfile: async ({
        uid = '',
        token = '',
        subMethod = ''
    } = {}) => {
        const currentMethod = 'UserService.getProfile';
        let response = getResponse({ type: 0 });
        try {
            const headers = { authorization: `bearer ${token}` };
            const data = {
                conditionals: [],
                ordersBy: [],
                method: methods?.accounts?.profile,
                id: uid,
            };
            response = await axiosV2?.post({
                url: `${urls?.url}${urls?.apiV1}${urls?.accounts}/`,
                data: data,
                headers,
                subMethod: currentMethod
            });
            return response;
        } catch (ex) {
            return getError({ ex: ex, response: response, currentMethod_: currentMethod, subMethod_: subMethod });
        }
    },

    /**
     * Update user account (profile)
     * @param {Object} params
     * @param {string} params.id
     * @param {Object} params.data
     * @param {string} params.token
     * @param {string} params.subMethod
     * @returns {Promise<Object>}
     */
    update: async ({
        id = '',
        data = {},
        token = '',
        subMethod = ''
    } = {}) => {
        const currentMethod = 'UserService.update';
        let response = getResponse({ type: 0 });
        try {
            const headers = { authorization: `bearer ${token}` };
            response = await axiosV2?.post({
                url: `${urls?.url}${urls?.apiV1}${urls?.accounts}/${id}`,
                data: data,
                headers,
                subMethod: currentMethod
            });
            return response;
        } catch (ex) {
            return getError({ ex: ex, response: response, currentMethod_: currentMethod, subMethod_: subMethod });
        }
    },

    /**
     * Change account password
     * @param {Object} params
     * @param {string} params.email
     * @param {string} params.currentPassword
     * @param {string} params.newPassword
     * @param {string} params.token
     * @param {string} params.subMethod
     * @returns {Promise<Object>}
     */
    changePassword: async ({
        email = '',
        currentPassword = '',
        newPassword = '',
        token = '',
        subMethod = ''
    } = {}) => {
        const currentMethod = 'UserService.changePassword';
        let response = getResponse({ type: 0 });
        try {
            const headers = { authorization: `bearer ${token}` };
            const data = {
                conditionals: [],
                email: email,
                method: methods?.accounts?.changePassword,
                ordersBy: [],
                password: currentPassword,
                passwordNew: newPassword,
            };
            response = await axiosV2?.post({
                url: `${urls?.url}${urls?.apiV1}${urls?.accounts}/`,
                data: data,
                headers,
                subMethod: currentMethod
            });
            return response;
        } catch (ex) {
            return getError({ ex: ex, response: response, currentMethod_: currentMethod, subMethod_: subMethod });
        }
    },

    /**
     * Upload a new file (no previous file)
     * @param {Object} params
     * @param {string} params.folder
     * @param {string} params.fileName
     * @param {string} params.fileString
     * @param {string} params.token
     * @param {string} params.subMethod
     * @returns {Promise<Object>}
     */
    fileUpload: async ({
        folder = '',
        fileName = '',
        fileString = '',
        token = '',
        subMethod = ''
    } = {}) => {
        const currentMethod = 'UserService.fileUpload';
        let response = getResponse({ type: 0 });
        try {
            const headers = { authorization: `bearer ${token}` };
            const data = {
                conditionals: [],
                ordersBy: [],
                folder: folder,
                fileName: fileName,
                fileString: fileString,
                fileFormat: 'data_url',
                fileFormat_: 'image',
                method: methods?.files?.uploadString,
            };
            response = await axiosV2?.post({
                url: `${urls?.url}${urls?.apiV1}${urls?.files}/`,
                data: data,
                headers,
                subMethod: currentMethod
            });
            return response;
        } catch (ex) {
            return getError({ ex: ex, response: response, currentMethod_: currentMethod, subMethod_: subMethod });
        }
    },

    /**
     * Upload replacing an existing file
     * @param {Object} params
     * @param {string} params.folder
     * @param {string} params.fileName
     * @param {string} params.fileString
     * @param {string} params.token
     * @param {string} params.subMethod
     * @returns {Promise<Object>}
     */
    fileUploadReplace: async ({
        folder = '',
        fileName = '',
        fileString = '',
        token = '',
        subMethod = ''
    } = {}) => {
        const currentMethod = 'UserService.fileUploadReplace';
        let response = getResponse({ type: 0 });
        try {
            const headers = { authorization: `bearer ${token}` };
            const data = {
                conditionals: [],
                ordersBy: [],
                folder: folder,
                fileName: fileName,
                fileString: fileString,
                fileFormat: 'data_url',
                fileFormat_: 'image',
                method: methods?.files?.uploadDeleteString,
            };
            response = await axiosV2?.post({
                url: `${urls?.url}${urls?.apiV1}${urls?.files}/`,
                data: data,
                headers,
                subMethod: currentMethod
            });
            return response;
        } catch (ex) {
            return getError({ ex: ex, response: response, currentMethod_: currentMethod, subMethod_: subMethod });
        }
    },

    /**
     * Send email verification
     * @param {Object} params
     * @param {string} params.id
     * @param {string} params.token
     * @param {string} params.subMethod
     * @returns {Promise<Object>}
     */
    sendEmailVerification: async ({
        id = '',
        token = '',
        subMethod = ''
    } = {}) => {
        const currentMethod = 'UserService.sendEmailVerification';
        let response = getResponse({ type: 0 });
        try {
            const headers = { authorization: `bearer ${token}` };
            const data = {
                conditionals: [],
                ordersBy: [],
                id: id,
                method: methods?.accounts?.sendEmailVerification,
            };
            response = await axiosV2?.post({
                url: `${urls?.url}${urls?.apiV1}${urls?.accounts}/`,
                data: data,
                headers,
                subMethod: currentMethod
            });
            return response;
        } catch (ex) {
            return getError({ ex: ex, response: response, currentMethod_: currentMethod, subMethod_: subMethod });
        }
    },

    /**
     * Set email verification status
     * @param {Object} params
     * @param {string} params.id
     * @param {string} params.token
     * @param {string} params.subMethod
     * @returns {Promise<Object>}
     */
    emailVerificationSet: async ({
        id = '',
        token = '',
        subMethod = ''
    } = {}) => {
        const currentMethod = 'UserService.emailVerificationSet';
        let response = getResponse({ type: 0 });
        try {
            const headers = { authorization: `bearer ${token}` };
            const data = {
                conditionals: [],
                ordersBy: [],
                id: id,
                method: methods?.accounts?.emailVerificationSet,
            };
            response = await axiosV2?.post({
                url: `${urls?.url}${urls?.apiV1}${urls?.accounts}/`,
                data: data,
                headers,
                subMethod: currentMethod
            });
            return response;
        } catch (ex) {
            return getError({ ex: ex, response: response, currentMethod_: currentMethod, subMethod_: subMethod });
        }
    },

    /**
     * Authorize user account
     * @param {Object} params
     * @param {string} params.id
     * @param {string} params.userId
     * @param {string} params.token
     * @param {string} params.subMethod
     * @returns {Promise<Object>}
     */
    authorized: async ({
        id = '',
        userId = '',
        token = '',
        subMethod = ''
    } = {}) => {
        const currentMethod = 'UserService.authorized';
        let response = getResponse({ type: 0 });
        try {
            const headers = { authorization: `bearer ${token}` };
            const data = {
                conditionals: [],
                ordersBy: [],
                id: id,
                userId: userId,
                method: methods?.accounts?.authorized,
            };
            response = await axiosV2?.post({
                url: `${urls?.url}${urls?.apiV1}${urls?.accounts}/`,
                data: data,
                headers,
                subMethod: currentMethod
            });
            return response;
        } catch (ex) {
            return getError({ ex: ex, response: response, currentMethod_: currentMethod, subMethod_: subMethod });
        }
    },

    /**
     * Send welcome email
     * @param {Object} params
     * @param {string} params.id
     * @param {string} params.token
     * @param {string} params.subMethod
     * @returns {Promise<Object>}
     */
    welcomeEmailSend: async ({
        id = '',
        token = '',
        subMethod = ''
    } = {}) => {
        const currentMethod = 'UserService.welcomeEmailSend';
        let response = getResponse({ type: 0 });
        try {
            const headers = { authorization: `bearer ${token}` };
            const data = {
                conditionals: [],
                ordersBy: [],
                id: id,
                method: methods?.accounts?.welcomeEmailSend || 'welcomeEmailSend',
            };
            response = await axiosV2?.post({
                url: `${urls?.url}${urls?.apiV1}${urls?.accounts}/`,
                data: data,
                headers,
                subMethod: currentMethod
            });
            return response;
        } catch (ex) {
            return getError({ ex: ex, response: response, currentMethod_: currentMethod, subMethod_: subMethod });
        }
    },
};
